import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { PaymentProcessor } from '@rbilabs/common';
import { VisuallyHidden } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';
import Measure from 'react-measure';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { ICartEntry } from '@rbi-ctg/menu';
import AddExtrasModal from 'components/add-extras-modal';
import { AddPaymentMethodModal } from 'components/add-payment-method-modal';
import CartError from 'components/cart-error';
import CartLoading from 'components/cart-loading';
import FullPageScreen from 'components/full-page-screen';
import { TransferGiftCardModal } from 'components/gift-card-modal';
import { AddGiftCardModal } from 'components/gift-card-modal/add-gift-card';
import { SuccessGiftCardModal } from 'components/gift-card-modal/success-gift-card';
import { SuccessTransferGiftCardModal } from 'components/gift-card-modal/success-transfer-gift-card';
import { MobileSpacer } from 'components/mobile-spacer';
import OrderUnavailableModal from 'components/modal-order-unavailable';
import PickupModeModal from 'components/pickup-mode-modal';
import useAuth from 'hooks/auth/use-auth';
import useAuthRedirects from 'hooks/auth/use-auth-redirects';
import { useLoyaltyActiveCard } from 'hooks/loyalty';
import { ICatering, usePriceOrder } from 'hooks/price-order';
import useDebounceEffect from 'hooks/use-debounce-effect';
import useFeatureMenuAddOn from 'hooks/use-feature-menu-add-on';
import useInterval from 'hooks/use-interval';
import useMediaQuery, { MediaQuery } from 'hooks/use-media-query';
import { LoyaltyRewardsBalance } from 'pages/loyalty/loyalty-rewards-balance';
import { selectors, useAppSelector } from 'state/global-state';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { isDiscountOffer } from 'state/loyalty/utils';
import { useNewMainMenuContext } from 'state/main-menu';
import { useOffersContext } from 'state/offers';
import { OrderStatus, useOrderContext } from 'state/order';
import { OrderSuccessFailureStatuses } from 'state/order/constants';
import { usePaymentContext } from 'state/payment';
import { CardTypes, IPaymentMethod } from 'state/payment/types';
import { useRestaurantPosConnectivityStatus } from 'state/restaurant-connectivity-status';
import { useStoreContext } from 'state/store';
import { useThRewardsManagementContext } from 'state/th-rewards-management';
import { createCartEntry } from 'utils/cart';
import { redeemReward } from 'utils/cart/redeem-reward';
import { mapPaymentMethodCardTypes } from 'utils/payment/map-payment-method-card-type';
import { isApplePay, isGooglePay } from 'utils/payment/native-payment';
import { relativeRoutes, routes } from 'utils/routing';
import { isCatering as isCateringOrder } from 'utils/service-mode';
import { toast } from 'utils/toast';

import CartContent from './cart-content';
import CartPageContainer from './cart-page-container';
import { LOYALTY_BARCODE_PREPAID_PAYMENT_TYPE_ID } from './constants';
import Content from './content';
import { useDeliveryErrorModalCopy } from './hooks/use-delivery-error-modal-copy';
import { HostedPagePayment } from './payment/order-payment/hosted-page-payment/hosted-page-payment';
import { PaymarkHostedPagePayment } from './payment/order-payment/paymark-hosted-page-payment/paymark-hosted-page-payment';
import { Payment } from './payment/order-payment/payment';
import { PaymentComponentMapping } from './payment/types';
import { CartContentWrapper, HeadingSuperText } from './styled';
import {
  IErrorsListItems,
  LoyaltyBarcodePaymentType,
  getServiceModeHeaderText,
  serviceModeLoyaltyBarcodeIds,
} from './types';
import { mapEntryTypeToOfferType } from './utils';
import { useTotalTipCalculations } from './your-cart/totals/use-total-tip-calculations';

export const getLoyaltyBarcodePaymentTypeId = (paymentMethod: IPaymentMethod) => {
  if (isApplePay(paymentMethod.fdAccountId)) {
    return LoyaltyBarcodePaymentType.APPLE_PAY;
  }
  if (isGooglePay(paymentMethod.fdAccountId)) {
    return LoyaltyBarcodePaymentType.GOOGLE_PAY;
  }

  if (paymentMethod.credit) {
    const cardType = mapPaymentMethodCardTypes(paymentMethod.credit.cardType);
    switch (cardType) {
      case CardTypes.AMEX:
        return LoyaltyBarcodePaymentType.AMEX;
      case CardTypes.VISA:
        return LoyaltyBarcodePaymentType.VISA;
      case CardTypes.MASTERCARD:
        return LoyaltyBarcodePaymentType.MASTERCARD;
      case CardTypes.DISCOVER:
        return LoyaltyBarcodePaymentType.DISCOVER;
      default:
        break;
    }
  }

  if (paymentMethod.prepaid) {
    return LOYALTY_BARCODE_PREPAID_PAYMENT_TYPE_ID;
  }

  return null;
};

export { CartPageContainer, Content, HeadingSuperText };

const Cart: React.FC = () => {
  const { formatMessage } = useIntl();
  const { user, isAuthenticated, refreshCurrentUser } = useAuth();
  const { locale: customerLocale } = useLocale();
  const orderCtx = useOrderContext();
  const {
    cartEntries: orderContextCartEntries,
    calculateCartTotalWithDiscount,
    cartVersion,
    deliveryAddress,
    deliveryInstructions,
    orderPhoneNumber,
    serviceMode: orderContextServiceMode,
    setIsPricingOrder,
    cateringNotes,
    cateringNumberOfGuests,
    cateringOrganizerName,
    cateringPickupDateTime,
    cateringShouldIncludeUtensils,
    cateringEmail,
  } = orderCtx;
  // Retrieve payment processor to determine which payment page to display
  const {
    paymentProcessor,
    setCheckoutPaymentMethodId,
    checkoutPaymentMethod,
  } = usePaymentContext();
  const offersCtx = useOffersContext();
  const { selectedOffer: selectedCBAOffer, selectedOfferCartEntry, selectedOfferPrice } = offersCtx;
  const enableSignupAfterCart = useFlag(LaunchDarklyFlag.ENABLE_MOVE_SIGNUP_AFTER_CART);
  const enableMenuServiceData = useFlag(LaunchDarklyFlag.ENABLE_MENU_SERVICE_DATA);
  const { isStoreOpenAndAvailable, store } = useStoreContext();
  const { getStoreMenu, storeMenuLoading, storeMenuCalled } = useNewMainMenuContext();
  const { region } = useLocale();
  const { activeCard } = useLoyaltyActiveCard();
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);

  const [cartContentInsetTop, setCartContentInsetTop] = useState(0);

  const orderPriceRefreshIntervalMs = Number(
    useFlag(LaunchDarklyFlag.ORDER_PRICE_REFRESH_INTERVAL_MS)
  );
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isPaymentActive = pathname.includes('/payment');
  const [cardSuccessfullyAdded, setCardSuccessfullyAdded] = useState(false);

  const storeId = store.number || '';

  const {
    isRestaurantPosOnline,
    loading: isRestaurantPosConnectivityStatusLoading,
  } = useRestaurantPosConnectivityStatus({
    storeId,
  });

  const { sections: addOnSections } = useFeatureMenuAddOn();

  const { isRedemptionEnabled } = useThRewardsManagementContext();
  const rewardChanged = user?.details?.loyaltyTier;

  const isMobile = useMediaQuery(MediaQuery.Mobile);

  const appliedOffersMap = useMemo(
    () =>
      appliedOffers.reduce((acc, offer) => {
        acc[offer.cartId || ''] = offer;
        return acc;
      }, {}),
    [appliedOffers]
  );

  const cartEntries = useMemo(() => {
    // Create the cart entry from the selected offer cart entry
    const offerMenuCartEntry = selectedOfferCartEntry
      ? [
          {
            ...selectedOfferCartEntry,
            price: selectedOfferPrice,
            offerVendorConfigs: selectedCBAOffer && selectedCBAOffer.vendorConfigs,
            type: mapEntryTypeToOfferType(selectedOfferCartEntry.type),
          },
        ]
      : [];
    // Create the cart entry from the selected discount offer
    // This offer will be available if the cart is not empty
    const selectedOffer = selectedCBAOffer;
    const offerDiscountCartEntry =
      orderContextCartEntries.length && selectedOffer && isDiscountOffer(selectedOffer)
        ? [
            {
              ...createCartEntry({ item: selectedOffer }),
              cartId: 'discount-offer',
              vendorConfigs: undefined,
              offerVendorConfigs: selectedOffer.vendorConfigs,
            },
          ]
        : [];

    // Updating entry type if its part of an applied offer
    const updatedCartEntries = (orderContextCartEntries || []).map((entry: ICartEntry) =>
      appliedOffersMap[entry.cartId]
        ? { ...entry, type: mapEntryTypeToOfferType(entry.type) }
        : entry
    );

    // Cart entries with offers
    return updatedCartEntries.concat(offerMenuCartEntry, offerDiscountCartEntry);
  }, [
    appliedOffersMap,
    orderContextCartEntries,
    selectedCBAOffer,
    selectedOfferCartEntry,
    selectedOfferPrice,
  ]);

  useEffect(() => {
    if (!enableMenuServiceData || !storeId) {
      return;
    }

    getStoreMenu(region, storeId);
  }, [enableMenuServiceData, getStoreMenu, region, storeId]);

  const {
    orderStatus,
    priceOrder,
    priceOrderFailure,
    priceOrderSuccess,
    priceResult: { error: priceMutationError },
    serverOrder,
    unavailableCartEntries,
  } = usePriceOrder({
    cartEntries,
  });

  const [showUnavailableItemsModal, setShowUnavailableItemsModal] = useState(false);

  // DO NOT USE service mode on order context. Use service mode on server order
  const orderServiceMode = serverOrder?.cart?.serviceMode;

  const serviceModeHeaderText = getServiceModeHeaderText(formatMessage);

  // Don't redirect to SignIn/SignUp if:
  // - The flag enable-signup-after-cart is enabled
  useAuthRedirects({ skip: enableSignupAfterCart });

  useEffect(() => {
    return () => {
      if (deliveryAddress?.shouldSave) {
        refreshCurrentUser();
      }
    };
  }, [deliveryAddress, refreshCurrentUser]);

  const { barcode = '' } = activeCard || {};

  const { rewardDiscountAmount = 0, additionalDiscountAmount = 0 } = useTotalTipCalculations({
    serverOrder,
    serviceMode: orderContextServiceMode,
  });

  const { cartTotal, isCartTotalNegative } = calculateCartTotalWithDiscount();
  const totalAppliedDiscount = rewardDiscountAmount + additionalDiscountAmount;

  const tryPricing = useCallback(
    async (overrideOrderParams?: { customerName?: string; tipCents?: number }) => {
      /**
       * Sample barcode composition looks like:
       * 046300...|F78B55B3...|13FF07A7DC0C...|MA|DT:TO|PT:TC
       * where MA means mobile app, DT is the dining type and PT is the payment type
       * https://rbictg.atlassian.net/browse/CBA-12104
       */
      const serviceModeBarcodeId = serviceModeLoyaltyBarcodeIds[orderContextServiceMode];
      const serviceModeBarcodeSuffix = serviceModeBarcodeId ? `|DT:${serviceModeBarcodeId}` : '';
      const paymentTypeBarcodeId =
        checkoutPaymentMethod && getLoyaltyBarcodePaymentTypeId(checkoutPaymentMethod);
      const paymentTypeBarcodeSuffix = paymentTypeBarcodeId ? `|PT:${paymentTypeBarcodeId}` : '';
      const loyaltyBarcode =
        barcode && `${barcode}|MA${serviceModeBarcodeSuffix}${paymentTypeBarcodeSuffix}`;

      if (
        !isStoreOpenAndAvailable ||
        !user ||
        (redeemReward && !loyaltyBarcode) ||
        isCartTotalNegative
      ) {
        return;
      }

      const validAppliedOffers = appliedOffers.map(({ id, cartId, swap, type, cmsId }) => ({
        id,
        cartId,
        swap,
        type,
        sanityId: cmsId,
      }));

      const customerName = null;
      const orderParams = {
        rewardsApplied: [],
        cartVersion,
        customerLocale,
        customerName,
        deliveryAddress,
        orderPhoneNumber,
        deliveryInstructions,
        loyaltyBarcode,
        appliedOffers: validAppliedOffers,
        // Reward discounts are already applied to items causing the requested amount to be less than the real amount since
        // We are substracting the discount twice (on the item and again on the discount)
        requestedAmountCents: cartTotal - totalAppliedDiscount + rewardDiscountAmount,
        serviceMode: orderContextServiceMode,
        store,
        isStoreOpenAndAvailable,
        ...overrideOrderParams,
      };

      setIsPricingOrder(true);
      if (isCateringOrder(orderContextServiceMode)) {
        const catering: ICatering = {
          notes: cateringNotes,
          numberOfGuests: cateringNumberOfGuests,
          organizerName: cateringOrganizerName,
          pickupTime: cateringPickupDateTime,
          includeUtensils: cateringShouldIncludeUtensils,
          pickupPhoneNumber: orderPhoneNumber,
          pickupEmail: cateringEmail,
        };
        void priceOrder(orderParams, user, catering);
      } else {
        void priceOrder(orderParams, user);
      }
      setIsPricingOrder(false);
    },
    [
      orderContextServiceMode,
      checkoutPaymentMethod,
      barcode,
      isStoreOpenAndAvailable,
      user,
      isCartTotalNegative,
      appliedOffers,
      cartVersion,
      customerLocale,
      deliveryAddress,
      orderPhoneNumber,
      deliveryInstructions,
      cartTotal,
      totalAppliedDiscount,
      rewardDiscountAmount,
      store,
      cateringPickupDateTime,
      cateringNotes,
      cateringNumberOfGuests,
      cateringOrganizerName,
      cateringShouldIncludeUtensils,
      cateringEmail,
      setIsPricingOrder,
      priceOrder,
    ]
  );

  const handleUnavailableModalDismiss = useCallback(() => {
    setShowUnavailableItemsModal(false);
    tryPricing();
  }, [tryPricing, setShowUnavailableItemsModal]);

  useEffect(() => {
    setShowUnavailableItemsModal(!!unavailableCartEntries.length);
  }, [unavailableCartEntries.length]);

  useEffect(() => {
    if (!orderContextServiceMode) {
      navigate(`${routes.cart}/${relativeRoutes.pickupMode}`, { replace: true });
    }

    if (!isStoreOpenAndAvailable) {
      const storeLocatorRoute = isCateringOrder(orderContextServiceMode)
        ? routes.cateringStoreLocator
        : routes.storeLocator;

      navigate(storeLocatorRoute);
    }
  }, [isStoreOpenAndAvailable, navigate, orderContextServiceMode]);

  useDebounceEffect(
    () => {
      if (!isStoreOpenAndAvailable || !orderContextServiceMode) {
        return;
      }
      tryPricing();
    },
    250,
    [
      appliedOffers,
      barcode,
      cartEntries,
      isRedemptionEnabled,
      isStoreOpenAndAvailable,
      orderContextServiceMode,
      selectedCBAOffer,
      selectedOfferCartEntry,
      rewardChanged,
    ]
  );

  useInterval(tryPricing, orderPriceRefreshIntervalMs || null, [tryPricing]);

  const hasPriceError = !!priceMutationError || priceOrderFailure;

  const showCartErrorModal =
    hasPriceError && !unavailableCartEntries.length && !isCartTotalNegative;
  const unavailableCartItems = unavailableCartEntries.length > 0;
  const errorsList: IErrorsListItems = [
    { priceMutationError },
    { priceOrderFailure },
    { unavailableCartItems },
    { isRestaurantPosOnline: !isRestaurantPosOnline },
    { isStoreOpenAndAvailable: !isStoreOpenAndAvailable },
  ];
  const { errorModalBody, errorModalTitle } = useDeliveryErrorModalCopy(serverOrder, errorsList);
  if (!isRestaurantPosOnline) {
    return <CartError startOver body={errorModalBody} title={errorModalTitle} />;
  }

  // Redirect to Menu if:
  // - Cart is empty
  // - Selected offer is null
  // - The order has not been inserted or has been requested
  if (
    (!cartEntries.length &&
      !selectedOfferCartEntry &&
      !OrderSuccessFailureStatuses.success.pos.includes(orderStatus as OrderStatus) &&
      !OrderSuccessFailureStatuses.inProgress.pos.includes(orderStatus as OrderStatus) &&
      !OrderSuccessFailureStatuses.success.catering.includes(orderStatus as OrderStatus)) ||
    (!orderContextServiceMode && !orderServiceMode)
  ) {
    navigate(routes.menu);
    return null;
  }

  // Show the loading cart if:
  // - User is authenticated or flag enable-signup-after-cart is disabled
  // - Is restaurant online or is pricing
  // - Is the Store Menu Data loading or is menu service data disabled
  if (
    !isCartTotalNegative &&
    (isAuthenticated() || !enableSignupAfterCart) &&
    isRestaurantPosConnectivityStatusLoading &&
    (!storeMenuCalled || storeMenuLoading || !enableMenuServiceData)
  ) {
    return <CartLoading />;
  }

  if (isMobile && (showCartErrorModal || showUnavailableItemsModal)) {
    if (showUnavailableItemsModal) {
      return (
        <OrderUnavailableModal
          items={unavailableCartEntries}
          onDismiss={handleUnavailableModalDismiss}
        />
      );
    }
    return <CartError startOver body={errorModalBody} title={errorModalTitle} />;
  }

  const handleSelect = (newFdAccountId?: string) => {
    if (!newFdAccountId) {
      setCheckoutPaymentMethodId('');
      return;
    }

    setCheckoutPaymentMethodId(newFdAccountId);
  };

  const CartAddGiftCardModal = (
    <AddGiftCardModal
      handleSelect={handleSelect}
      onCardAdded={() => setCardSuccessfullyAdded(true)}
    />
  );

  const paymentPageMapping: PaymentComponentMapping = {
    [PaymentProcessor.VRPAYMENT]: HostedPagePayment,
    [PaymentProcessor.PAYMARK]: PaymarkHostedPagePayment,
    [PaymentProcessor.ADYEN]: Payment,
    [PaymentProcessor.FIRSTDATA]: Payment,
    [PaymentProcessor.CHASE]: Payment,
    [PaymentProcessor.ORBITAL]: Payment,
  };

  const CartOrderPayment = () => {
    if (!paymentProcessor) {
      return null;
    }

    // Determines which type of payment page to display based on the payment processor
    const OrderPayment = paymentPageMapping[paymentProcessor];

    return (
      <>
        <OrderPayment
          cartEntries={cartEntries}
          onPriceOrder={tryPricing}
          pricedAndAvailable={
            priceOrderSuccess && !unavailableCartEntries.length && !priceMutationError
          }
          serverOrder={serverOrder}
        />
        <Routes>
          <Route
            path={relativeRoutes.addCard}
            element={
              <AddPaymentMethodModal
                onCardAdded={() =>
                  toast.success(
                    formatMessage({ id: 'yourNewPaymentMethodHasBeenSuccessfullySaved' })
                  )
                }
              />
            }
          />
          <Route path={relativeRoutes.addGiftCard} element={CartAddGiftCardModal} />
        </Routes>
      </>
    );
  };

  return (
    <>
      <div aria-hidden={isPaymentActive || undefined} {...(isPaymentActive ? { inert: '' } : {})}>
        <FullPageScreen heading={serviceModeHeaderText[orderContextServiceMode]} closeHref="/menu">
          <Measure
            offset
            onResize={({ offset }) => {
              setCartContentInsetTop(offset?.height || 0);
            }}
          >
            {({ measureRef }) => <LoyaltyRewardsBalance ref={measureRef} />}
          </Measure>
          <CartContentWrapper insetTop={cartContentInsetTop}>
            <CartContent
              serverOrder={serverOrder}
              cartEntries={cartEntries}
              hasAddOns={addOnSections.length > 0}
              user={user}
              orderStatus={orderStatus}
              tryPricing={tryPricing}
              unavailableCartEntries={unavailableCartEntries}
              priceMutationError={priceMutationError}
              priceOrderSuccess={priceOrderSuccess}
              checkoutPaymentMethod={checkoutPaymentMethod}
            />
          </CartContentWrapper>
        </FullPageScreen>
      </div>

      {showCartErrorModal && <CartError startOver body={errorModalBody} title={errorModalTitle} />}
      {showUnavailableItemsModal && (
        <OrderUnavailableModal
          items={unavailableCartEntries}
          onDismiss={handleUnavailableModalDismiss}
        />
      )}

      {cardSuccessfullyAdded && (
        <VisuallyHidden>
          {formatMessage({ id: 'yourNewPaymentMethodHasBeenSuccessfullySaved' })}
        </VisuallyHidden>
      )}

      <Routes>
        <Route
          path={relativeRoutes.addCard}
          element={
            <AddPaymentMethodModal
              onCardAdded={() =>
                toast.success(formatMessage({ id: 'yourNewPaymentMethodHasBeenSuccessfullySaved' }))
              }
            />
          }
        />
        <Route path={relativeRoutes.addGiftCard} element={CartAddGiftCardModal} />
        <Route
          path={relativeRoutes.pickupMode}
          element={<PickupModeModal allowsDismiss={false} redirectUrl={routes.cart} />}
        />
        <Route path={relativeRoutes.addExtra} element={<AddExtrasModal addOns={addOnSections} />} />
        <Route
          path={relativeRoutes.transferGiftCard}
          element={
            <TransferGiftCardModal
              onCardTransfered={() =>
                toast.success(formatMessage({ id: 'transferBalanceCompletedMessage' }))
              }
            />
          }
        />
        <Route
          path={relativeRoutes.successTransferGiftCard}
          element={
            <SuccessTransferGiftCardModal onCardTransfered={() => setCardSuccessfullyAdded(true)} />
          }
        />
        <Route path={relativeRoutes.successGiftCard} element={<SuccessGiftCardModal />} />
        <Route path={`${relativeRoutes.orderPayment}/*`} element={CartOrderPayment()} />
      </Routes>
      <MobileSpacer />
    </>
  );
};

export default Cart;
